import { useState, useEffect } from 'react'
import { getBreakTimes, createBreakTime, updateBreakTime, deleteBreakTime } from '@/services/bookingApi'
import { BreakTime } from '@/types'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Loader2, Plus, Pencil, Trash2, X } from 'lucide-react'
import { useDashboardEmbedded } from '@/hooks/useDashboardEmbedded'

const DAY_LABELS: Record<number, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
}

interface BreakTimeFormData {
  type: 'recurring' | 'date_specific'
  day_of_week: number | null
  specific_date: string | null
  start_time: string
  end_time: string
  label: string
}

const initialFormData: BreakTimeFormData = {
  type: 'recurring',
  day_of_week: null,
  specific_date: null,
  start_time: '12:00',
  end_time: '13:00',
  label: '',
}

export default function BreakTimesEditor() {
  const embedded = useDashboardEmbedded()
  const [breakTimes, setBreakTimes] = useState<BreakTime[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formData, setFormData] = useState<BreakTimeFormData>(initialFormData)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    fetchBreakTimes()
  }, [])

  const fetchBreakTimes = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await getBreakTimes()
      setBreakTimes(data)
    } catch (err) {
      console.error('Failed to fetch break times:', err)
      setError('Failed to load break times')
    } finally {
      setIsLoading(false)
    }
  }

  const openAddModal = () => {
    setEditingId(null)
    setFormData(initialFormData)
    setIsModalOpen(true)
  }

  const openEditModal = (breakTime: BreakTime) => {
    setEditingId(breakTime.id)
    setFormData({
      type: breakTime.type,
      day_of_week: breakTime.day_of_week,
      specific_date: breakTime.specific_date,
      start_time: breakTime.start_time,
      end_time: breakTime.end_time,
      label: breakTime.label || '',
    })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingId(null)
    setFormData(initialFormData)
  }

  const handleSave = async () => {
    setIsSaving(true)
    setError(null)
    try {
      const payload = {
        type: formData.type,
        start_time: formData.start_time,
        end_time: formData.end_time,
        label: formData.label || undefined,
        ...(formData.type === 'recurring'
          ? { day_of_week: formData.day_of_week }
          : { specific_date: formData.specific_date }),
      }

      if (editingId) {
        await updateBreakTime(editingId, payload)
      } else {
        await createBreakTime(payload as Parameters<typeof createBreakTime>[0])
      }
      await fetchBreakTimes()
      closeModal()
    } catch (err) {
      console.error('Failed to save break time:', err)
      setError('Failed to save break time')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this break time?')) return

    try {
      await deleteBreakTime(id)
      await fetchBreakTimes()
    } catch (err) {
      console.error('Failed to delete break time:', err)
      setError('Failed to delete break time')
    }
  }

  const recurringBreaks = breakTimes.filter((bt) => bt.type === 'recurring')
  const dateSpecificBreaks = breakTimes.filter((bt) => bt.type === 'date_specific')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8 max-w-2xl">
      <div className={`${embedded ? 'mb-6' : 'mb-8'} flex items-center justify-between`}>
        {!embedded && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Break Times</h1>
            <p className="text-gray-500">Manage your recurring breaks and date-specific time off</p>
          </div>
        )}
        {embedded && <div />}
        <Button onClick={openAddModal}>
          <Plus className="w-4 h-4 mr-2" />
          Add Break
        </Button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Recurring Breaks Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Recurring Breaks</CardTitle>
          <CardDescription>Weekly breaks that repeat on the same day</CardDescription>
        </CardHeader>
        <CardContent>
          {recurringBreaks.length === 0 ? (
            <p className="text-gray-500 text-sm">No recurring breaks configured</p>
          ) : (
            <div className="space-y-3">
              {recurringBreaks.map((breakTime) => (
                <div
                  key={breakTime.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {DAY_LABELS[breakTime.day_of_week!] || 'Unknown Day'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {breakTime.start_time} - {breakTime.end_time}
                      {breakTime.label && ` • ${breakTime.label}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openEditModal(breakTime)}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(breakTime.id)}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Date-Specific Breaks Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Date-Specific Breaks</CardTitle>
          <CardDescription>Holidays and one-time breaks</CardDescription>
        </CardHeader>
        <CardContent>
          {dateSpecificBreaks.length === 0 ? (
            <p className="text-gray-500 text-sm">No date-specific breaks configured</p>
          ) : (
            <div className="space-y-3">
              {dateSpecificBreaks.map((breakTime) => (
                <div
                  key={breakTime.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">{breakTime.specific_date}</p>
                    <p className="text-sm text-gray-500">
                      {breakTime.start_time} - {breakTime.end_time}
                      {breakTime.label && ` • ${breakTime.label}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openEditModal(breakTime)}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(breakTime.id)}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingId ? 'Edit Break Time' : 'Add Break Time'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="p-1 text-gray-500 hover:text-gray-700 rounded-md"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Type Toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({ ...formData, type: 'recurring', day_of_week: 1 })
                    }
                    className={`flex-1 py-2 px-3 rounded-md border transition-colors ${
                      formData.type === 'recurring'
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Recurring
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({ ...formData, type: 'date_specific', specific_date: '' })
                    }
                    className={`flex-1 py-2 px-3 rounded-md border transition-colors ${
                      formData.type === 'date_specific'
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Date-Specific
                  </button>
                </div>
              </div>

              {/* Day of Week Picker (for Recurring) */}
              {formData.type === 'recurring' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Day</label>
                  <select
                    value={formData.day_of_week ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        day_of_week: parseInt(e.target.value, 10),
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select a day</option>
                    {Object.entries(DAY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Date Picker (for Date-Specific) */}
              {formData.type === 'date_specific' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                  <Input
                    type="date"
                    value={formData.specific_date || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, specific_date: e.target.value })
                    }
                  />
                </div>
              )}

              {/* Time Pickers */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Time
                  </label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) =>
                      setFormData({ ...formData, start_time: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) =>
                      setFormData({ ...formData, end_time: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Label (optional)
                </label>
                <Input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="e.g., Lunch break"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t">
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : editingId ? (
                  'Update'
                ) : (
                  'Add'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}